import { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { CheckCircle, AlertCircle, AlertTriangle } from 'lucide-react';
import { useOrderSegment, type ParsedMultiXlsx } from '../../../../apps/hooks/oms/useOrderSegment';
import { useAuth } from '../../../../providers/AuthProvider';
import BrandCombobox from '../../../oms/order-segment/components/BrandCombobox';
import XlsxUploader from '../../../oms/order-segment/components/XlsxUploader';

interface SubmitResult {
  success: boolean;
  message: string;
  totalInserted: number;
}

function normalize(s: string) {
  return s.replace(/[^a-z0-9]/gi, '').toLowerCase();
}

export default function OrderSegmentTab() {
  const { brands, brandsLoading, parseMultiXlsx, submit, submitting } = useOrderSegment();
  const { user } = useAuth();

  const [orderSegment, setOrderSegment] = useState<'marketing' | 'partnership' | ''>('');
  const [selectedBrands, setSelectedBrands] = useState<typeof brands>([]);
  const [parsedResult, setParsedXlsx] = useState<ParsedMultiXlsx | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [submitResult, setSubmitResult] = useState<SubmitResult | null>(null);

  useEffect(() => {
    if (!parsedResult || parsedResult.mergedBrands.length === 0 || brands.length === 0) return;
    const matched = brands.filter(b =>
      parsedResult.mergedBrands.some(eb => normalize(eb) === normalize(b.name))
    );
    if (matched.length > 0) setSelectedBrands(matched);
  }, [parsedResult, brands]);

  const isFormValid =
    orderSegment !== '' &&
    parsedResult !== null &&
    selectedBrands.length > 0;

  const handleParse = async (files: File[]) => {
    setParsing(true);
    setParseError(null);
    setParsedXlsx(null);
    setSubmitResult(null);
    setSelectedBrands([]);
    try {
      const result = await parseMultiXlsx(files);
      setParsedXlsx(result);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Failed to parse file');
    } finally {
      setParsing(false);
    }
  };

  const handleClear = () => {
    setParsedXlsx(null);
    setParseError(null);
    setSubmitResult(null);
    setSelectedBrands([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid || !parsedResult || !user) return;

    setSubmitResult(null);
    const createdBy = user.email;
    const orderIds = parsedResult.mergedOrderIds;

    if (selectedBrands.length > 0) {
      let totalInserted = 0;
      const errors: string[] = [];

      for (const brand of selectedBrands) {
        const result = await submit({
          orderIds,
          orderSegment: orderSegment as 'marketing' | 'partnership',
          brandSlug: brand.slug,
          brandName: brand.name,
          createdBy,
        });
        if (result.success) {
          totalInserted += result.insertedCount;
        } else {
          errors.push(`${brand.name}: ${result.message}`);
        }
      }

      if (errors.length === 0) {
        const duplicatesSkipped = orderIds.length * selectedBrands.length - totalInserted;
        setSubmitResult({
          success: true,
          message: `Inserted ${totalInserted} rows for ${orderIds.length} orders across ${selectedBrands.length} brand(s).${duplicatesSkipped > 0 ? ` ${duplicatesSkipped} duplicates skipped.` : ''}`,
          totalInserted,
        });
      } else {
        setSubmitResult({
          success: false,
          message: `Some submissions failed: ${errors.join('; ')}`,
          totalInserted,
        });
      }
    } else {
      const result = await submit({
        orderIds,
        orderSegment: orderSegment as 'marketing' | 'partnership',
        brandSlug: null,
        brandName: null,
        createdBy,
      });
      setSubmitResult({
        success: result.success,
        message: result.message,
        totalInserted: result.insertedCount,
      });
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Row>
        <Col lg={5}>
          <Card className="mb-3">
            <Card.Header>Classify Orders as Marketing or Partnership</Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Order Segment</Form.Label>
                <Form.Select
                  value={orderSegment}
                  onChange={(e) => {
                    setOrderSegment(e.target.value as 'marketing' | 'partnership' | '');
                    setSubmitResult(null);
                  }}
                  required
                >
                  <option value="">Select a segment...</option>
                  <option value="marketing">Marketing</option>
                  <option value="partnership">Partnership</option>
                </Form.Select>
              </Form.Group>

              <Form.Group>
                <Form.Label>
                  Brand
                </Form.Label>
                <BrandCombobox
                  brands={brands}
                  selectedBrands={selectedBrands}
                  onChange={setSelectedBrands}
                  disabled={orderSegment === '' || brandsLoading}
                  required
                />
              </Form.Group>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={7}>
          <Card className="mb-3">
            <Card.Header>Upload Order IDs</Card.Header>
            <Card.Body>
              <XlsxUploader
                onParse={handleParse}
                parsedResult={parsedResult}
                parseError={parseError}
                parsing={parsing}
                onClear={handleClear}
              />
              {parsedResult && parsedResult.mergedBrands.length === 0 && (
                <Alert variant="warning" className="mt-3 mb-0">
                  <AlertTriangle className="me-2" size={16} />
                  Please fill in the brand name — no brand values found in the uploaded file.
                </Alert>
              )}
              {parsedResult && parsedResult.mergedBrands.length > 0 && selectedBrands.length === 0 && (
                <Alert variant="warning" className="mt-3 mb-0">
                  <AlertTriangle className="me-2" size={16} />
                  Brands found in file ({parsedResult.mergedBrands.join(', ')}) but none matched known brands. Please select manually.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {submitting && (
        <Alert variant="warning" className="mb-3">
          <Spinner animation="border" size="sm" className="me-2" />
          Submitting order IDs to BigQuery...
        </Alert>
      )}

      {!submitting && submitResult?.success && submitResult.totalInserted > 0 && (
        <Alert variant="success" className="mb-3">
          <CheckCircle className="me-2" size={16} />
          {submitResult.message}
        </Alert>
      )}

      {!submitting && submitResult?.success && submitResult.totalInserted === 0 && (
        <Alert variant="warning" className="mb-3">
          <AlertCircle className="me-2" size={16} />
          {submitResult.message}
        </Alert>
      )}

      {!submitting && submitResult && !submitResult.success && (
        <Alert variant="danger" className="mb-3">
          <AlertCircle className="me-2" size={16} />
          {submitResult.message}
        </Alert>
      )}

      <div className="d-flex justify-content-end">
        <Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={!isFormValid || submitting}
        >
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Submitting...
            </>
          ) : (
            'Submit'
          )}
        </Button>
      </div>
    </Form>
  );
}
